import React from "react";
import { Bullets, CTACard, Lead, P, Section } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "Designing an agent loop for coding";
export const excerpt =
  "If you have ever pasted a failing test back into a coding agent, you were the loop. Notes on designing that loop on purpose.";
export const heroAlt =
  "A winding river crossing a wide green plain toward the horizon at sunset";
export const readingTime = "6 min read";

const BLUE = "#5ba8f5";
const FAIL = "#e0795f";
/** Handwriting face (loaded in index.html) with safe fallbacks, for the sketched figures. */
const HAND = '"Architects Daughter", "Comic Sans MS", "Segoe Print", cursive';
/** Asymmetric border-radii that read as a hand-drawn rectangle; rotate through them for variety. */
const WONKY = [
  "255px 15px 225px 15px / 15px 225px 15px 255px",
  "15px 225px 18px 255px / 255px 18px 225px 15px",
  "225px 15px 255px 18px / 18px 255px 15px 225px",
  "15px 255px 15px 225px / 225px 15px 255px 18px",
];
const wonk = (i: number) => WONKY[((i % WONKY.length) + WONKY.length) % WONKY.length];
const tilt = (i: number) => [-0.6, 0.5, -0.4, 0.7, -0.3][i % 5] ?? 0;

function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/** A hand-drawn stage box in the loop diagram. */
function LoopBox({ i, title, note, accent }: { i: number; title: string; note: string; accent?: string }) {
  return (
    <div
      style={{
        padding: "10px 16px 12px",
        borderRadius: wonk(i),
        border: `2px solid ${accent ?? "var(--border-strong)"}`,
        background: "var(--surface-5)",
        transform: `rotate(${tilt(i) * 0.5}deg)`,
        textAlign: "center",
      }}
    >
      <div style={{ fontFamily: HAND, fontSize: 17, color: "var(--text-primary)" }}>{title}</div>
      <div style={{ fontFamily: HAND, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.4, marginTop: 1 }}>{note}</div>
    </div>
  );
}

/** A short sketched arrow pointing down, optionally rotated for a fork. */
function LoopArrow({ i = 0, angle = 0, label, labelColor }: { i?: number; angle?: number; label?: string; labelColor?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 7, height: 38 }}>
      <svg width="18" height="30" viewBox="0 0 18 30" aria-hidden style={{ flexShrink: 0, transform: `rotate(${angle + tilt(i)}deg)` }}>
        <path d="M9 2 C 7.5 8, 10.5 13, 9 20" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" />
        <path d="M4.5 19 L9 26 L13.5 19" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label && <span style={{ fontFamily: HAND, fontSize: 13.5, color: labelColor ?? "var(--text-muted)" }}>{label}</span>}
    </div>
  );
}

/**
 * The bare loop as a one-line sketch: the pipeline of words, plus a retry arc
 * from decide back up into attempt.
 */
function LoopStripFigure() {
  const arrow = (x: number, i: number) => (
    <g transform={`translate(${x} 0) rotate(${tilt(i) * 2} 12 37)`}>
      <path d="M1 37 C 8 35.5, 16 38.5, 23 37" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M16 32 L23 37 L16 42" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
  const word = (x: number, w: string, i: number) => (
    <text x={x} y={42} fontFamily={HAND} fontSize="17" fill="var(--text-primary)" transform={`rotate(${tilt(i) * 0.4} ${x} 42)`}>
      {w}
    </text>
  );
  return (
    <figure style={{ margin: "26px 0" }}>
      <div
        role="img"
        aria-label="Request, define goal, attempt, verify, decide, with a retry arc looping from decide back to attempt, carrying the evidence."
        style={{
          padding: "10px 14px 4px",
          borderRadius: wonk(1),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        <svg viewBox="0 0 500 138" style={{ display: "block", width: "100%", maxWidth: 560, height: "auto", margin: "0 auto" }} aria-hidden>
          {word(10, "request", 0)}
          {arrow(76, 0)}
          {word(106, "define goal", 1)}
          {arrow(206, 1)}
          {word(236, "attempt", 2)}
          {arrow(308, 2)}
          {word(338, "verify", 3)}
          {arrow(394, 3)}
          {word(424, "decide", 4)}

          <path
            d="M452 56 C 458 80, 428 93, 357 94 C 296 95, 275 88, 271 64"
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="1.8"
            strokeLinecap="round"
            opacity="0.85"
          />
          <path d="M264 69 L271 60 L278 69" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
          <text x="360" y="122" fontFamily={HAND} fontSize="13.5" fill="var(--text-secondary)" textAnchor="middle" transform="rotate(-0.5 360 122)">
            retry, carrying the evidence
          </text>
        </svg>
      </div>
    </figure>
  );
}

/**
 * The loop, sketched vertically: goal + check → attempt → observe → verify,
 * a loop-back gutter on the right carrying the failure evidence, and the two
 * terminal states at the bottom. Same hand-drawn look as the rest of the blog.
 */
function AgentLoopFigure() {
  return (
    <figure style={{ margin: "34px 0" }}>
      <div
        role="img"
        aria-label="The coding agent loop. A goal and check lead to an attempt, observation, and verification. A failed check loops back to a new attempt carrying the evidence. Passing ends in verified work; a stalled loop hands control back to a person."
        style={{
          padding: "26px 20px",
          borderRadius: wonk(0),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) clamp(64px, 16vw, 104px)",
            columnGap: 8,
            maxWidth: 480,
            margin: "0 auto",
          }}
        >
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={0} accent={BLUE} title="Goal + check" note="what done means, and the evidence that proves it" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={0} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={1} title="Attempt" note="the agent changes an isolated worktree" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={1} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={2} title="Observe" note="collect the diff, test output, and runtime state" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={2} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={3} title="Verify" note="hold the evidence against the check" />
          </div>

          {/* Loop-back gutter: from Verify up into Attempt, carrying the evidence. */}
          <div aria-hidden style={{ gridColumn: 2, gridRow: "3 / 8", position: "relative", margin: "26px 0" }}>
            <div
              style={{
                position: "absolute",
                inset: "0 6px 0 6px",
                border: "2px solid var(--text-muted)",
                borderLeft: "none",
                borderRadius: "0 30px 34px 0 / 0 44px 38px 0",
                opacity: 0.8,
              }}
            />
            <svg width="14" height="16" viewBox="0 0 14 16" style={{ position: "absolute", left: -4, top: -7 }}>
              <path d="M12 2 L2 8 L12 14" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
            </svg>
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: -4,
                transform: "translateY(-50%)",
                background: "var(--surface-4)",
                padding: "6px 0",
                fontFamily: HAND,
                fontSize: 13,
                lineHeight: 1.4,
                color: "var(--text-secondary)",
                textAlign: "center",
                width: "min(100%, 96px)",
              }}
            >
              not yet, go again with the evidence
            </span>
          </div>

          <div style={{ gridColumn: 1, display: "flex", justifyContent: "space-evenly" }}>
            <LoopArrow i={3} angle={26} label="pass" labelColor={BLUE} />
            <LoopArrow i={4} angle={-26} label="stuck" labelColor={FAIL} />
          </div>
          <div style={{ gridColumn: 1, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={{ flex: "1 1 170px" }}>
              <LoopBox i={2} accent={BLUE} title="Verified work" note="yours to review, merge, or discard" />
            </div>
            <div style={{ flex: "1 1 170px" }}>
              <LoopBox i={3} accent={FAIL} title="Back to a person" note="blocked, out of budget, or repeating itself" />
            </div>
          </div>
        </div>
      </div>
      <figcaption
        style={{ marginTop: 12, fontFamily: HAND, fontSize: 16, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.5 }}
      >
        The agent only occupies the Attempt box. The loop around it decides what counts as done, which evidence to trust, and when to stop.
      </figcaption>
    </figure>
  );
}

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "S. Macedo, “Stop Hand-Holding Your Coding Agent: Engineering the Loops that Replace Step-by-Step Prompting” (2026). Used for the distinction between internal agent cycles and external loop specifications, and for the trigger, goal, verification, stopping, and memory structure. This is a recent preprint based partly on a hand-coded corpus of 50 public loops.",
    href: "https://arxiv.org/abs/2607.00038",
    label: "arxiv.org/abs/2607.00038",
  },
  {
    text: "H. Guan et al., “SWE-Cycle: Benchmarking Code Agents across the Complete Issue Resolution Cycle” (2026). Used for the separation of environment reconstruction, implementation, verification-test generation, and complete-cycle execution.",
    href: "https://arxiv.org/abs/2605.13139",
    label: "arxiv.org/abs/2605.13139",
  },
  {
    text: "R. Wang et al., “SWE-Review: Closing the Loop on Issue Resolution with Agentic Code Review” (2026). Used for the generate-review-revise architecture and reported value of structured reviewer feedback. This is a very recent preprint.",
    href: "https://arxiv.org/abs/2607.06065",
    label: "arxiv.org/abs/2607.06065",
  },
  {
    text: "S. Shrivastava, “Semantic Early-Stopping for Iterative LLM Agent Loops” (2026). Used for the fixed-iteration critique and 38% operational-token result. The experiment covers 60 multi-hop QA questions, not coding tasks, so it is presented only as a stopping-policy example.",
    href: "https://arxiv.org/abs/2606.27009",
    label: "arxiv.org/abs/2606.27009",
  },
  {
    text: "X. Hou et al., “When Agents Do Not Stop: Uncovering Infinite Agentic Loops in LLM Agents” (2026). Used for the definition and repository scan of infinite agentic loops.",
    href: "https://arxiv.org/abs/2607.01641",
    label: "arxiv.org/abs/2607.01641",
  },
  {
    text: "0-AI-UG, “Cate Agent: job-card agent that drives coding CLIs in canvas worktrees” and the Cate Agent tool implementation (2026). Used for Cate’s goal, iteration, worktree, driver, verifier, and result-selection workflow.",
    href: "https://github.com/0-AI-UG/cate/pull/423",
    label: "github.com/0-AI-UG/cate/pull/423",
  },
];

export default function Post() {
  return (
    <>
      <Lead>A prompt tells a coding agent what to do once. Nearly everything that matters happens after the first result comes back wrong.</Lead>

      <P>
        Suppose you ask a coding agent to add CSV export to a reporting page. It reads the repository,
        edits a few files, runs a test, and reports success. The test fails, so you paste the error back
        in. The next version breaks the page, so you tell it to open the browser and look. The version
        after that works, but it also rewrote half the reporting module on the way.
      </P>
      <P>
        By the third round, you are the loop. You carry the evidence between attempts and you decide
        when the work is actually done.
      </P>
      <P>
        The question that interests me is how much of that can be written down precisely enough for the
        system to run it alone, and where it cannot be, so that the work comes back to a person instead
        of circling forever.
      </P>

      <Section title="The loop outside the agent">
        <P>
          The word <em>loop</em> is doing a lot of work in that last paragraph, so it helps to pull the
          senses apart. There is the ordinary loop inside a program. There is the internal cycle most
          agents already run on their own: look at the environment, pick an action, call a tool, look at
          the result, keep going.
        </P>
        <P>The one I care about here is a third loop, and it sits outside the model:</P>
        <LoopStripFigure />
        <P>
          A recent preprint calls this a <em>loop specification</em>: a trigger, a goal, some execution, a
          verification step, a rule for when to stop, and memory carried between rounds.<Fn n={1} /> The
          name is new but none of the parts are. Continuous integration has done a version of this for
          years. It runs when something happens, checks the result, records what it saw, and ends in a
          named state. The one thing an agent adds is a model that can rewrite its own plan between runs.
        </P>
      </Section>

      <Section title="Start with the condition for stopping">
        <P>
          “Implement CSV export” describes an activity. It does not describe when the activity is complete.
        </P>
        <Bullets
          items={[
            <><strong>Goal:</strong> A user can export the currently filtered report as a correctly encoded CSV file.</>,
            <><strong>Check:</strong> Run the export tests, verify the build, and inspect the diff for changes outside the reporting feature.</>,
          ]}
        />
        <P>
          The split is the whole point. The goal is what should become true. The check is the evidence the
          loop will actually use to decide whether it did.
        </P>
        <P>
          Collapse the two and the agent ends up as its own author, tester, and judge. It will happily
          declare victory because the code looks reasonable, because a command exited zero, or because it
          has simply run out of ideas. None of those mean the feature works.
        </P>
        <P>
          It helps to treat verification as a ladder rather than a single gate. A self-report from the agent
          is the cheap bottom rung, and on its own it is worth almost nothing. A clean static check and a
          passing build tell you more. Targeted tests tell you more again, because they exercise the behavior
          you actually asked for. Running the real product surfaces the integration failures that no unit test
          was ever going to catch, and a separate review picks up the things the implementation agent has
          quietly trained itself to skip.
        </P>
        <P>
          Recent coding-agent research is pushing the same way. SWE-Cycle pulls apart environment setup,
          implementation, and test generation, then measures a sharp drop in performance once agents have to
          run the whole cycle themselves.<Fn n={2} /> SWE-Review bolts a separate reviewer onto a
          generate-review-revise loop and finds that structured review feedback makes later revisions better
          than one-shot generation does.<Fn n={3} />
        </P>
        <P>
          The verifier does not have to be another model. When a deterministic test can settle it, use the
          test. A model earns its place only when the check needs judgment: did the diff stay inside the
          feature, does the UI actually match what was asked, is this workaround a tradeoff you would accept.
        </P>
      </Section>

      <Section title="The structure of a coding loop">
        <P>
          An attempt changes the environment, observation writes down what happened, and verification holds
          that evidence up against the goal. A failed check goes one of two ways: it either hands useful
          evidence to the next attempt, or it exits into a named terminal state.
        </P>
        <AgentLoopFigure />
        <P>
          The agent is one box in this diagram. How the whole thing behaves depends just as much on the
          verifier, on the state you carry between attempts, on the retry policy, and on the rules that
          decide when the loop is allowed to end.
        </P>
      </Section>

      <Section title="A retry needs new information">
        <P>Feeding the agent the same prompt again is technically a loop. It is just not a loop that gets anywhere.</P>
        <P>
          Every failed attempt should hand something concrete to the next one: a failing assertion, a
          screenshot, a compiler error, a reviewer comment, or even a plain sentence about why the goal was
          missed. The next round should start from that, not from a fresh copy of the original request.
        </P>
        <P>
          Isolating attempts helps more than I expected it to. A fresh branch or worktree gives each attempt
          a clean starting point, keeps them comparable, and stops an abandoned approach from leaking into the
          one that follows. Run several attempts at once and each one needs its own workspace anyway. Then the
          loop can keep whichever result actually verified instead of trying to stitch half-compatible diffs
          together.
        </P>
        <P>
          Memory is worth being picky about. The loop needs the goal, the previous verdicts, the decisions
          that mattered, and the current state of the repository. It almost never needs the full transcript
          of every tool call. Keeping everything just makes the next round longer, not smarter.
        </P>
      </Section>

      <Section title="Stopping is part of correctness">
        <P>Every loop needs explicit terminal states. At minimum:</P>
        <Bullets
          items={[
            <><strong>success:</strong> the check passed;</>,
            <><strong>blocked:</strong> progress requires a person or missing external information;</>,
            <><strong>exhausted:</strong> the attempt or cost limit was reached;</>,
            <><strong>cancelled:</strong> the user stopped the work;</>,
            <><strong>error:</strong> the loop itself could not continue safely.</>,
          ]}
        />
        <P>
          A cap on attempts is still worth having, but treat it as a safety bound and not as a definition of
          done. Work on iterative language-model systems suggests fixed iteration counts burn effort long after
          the output has stopped getting better. In one small non-coding experiment, a semantic stopping rule
          cut operational tokens by 38 percent at roughly the same measured quality.<Fn n={4} /> That number
          does not carry straight over to software, but the lesson behind it does: watch whether the loop is
          making progress, not just how many times it has gone around.
        </P>
        <P>
          The opposite failure is the loop that never hits a real bound at all. A July 2026 static-analysis
          study looked at 6,549 agent repositories and confirmed 68 likely infinite-agent-loop defects across
          47 projects.<Fn n={5} /> In each case the loop kept reaching model calls, tools, retries, or handoffs
          without any stopping condition that covered the whole feedback path.
        </P>
        <P>
          For coding, a workable no-progress rule might trip when two attempts fail the same check for the same
          reason, when the diff stops changing in any meaningful way, or when the next step would need authority
          the agent was never given.
        </P>
      </Section>

      <Section title="A loop should remain inspectable">
        <P>
          Handing more of the work to the machine does not shrink how much its internal state matters. It turns
          that state into about the only thing you can still supervise.
        </P>
        <P>
          While a loop runs, a developer should be able to answer plain questions about it. What is the goal
          right now? Which attempt is live? What changed? Which check is running? Why did the last attempt fail,
          and what happens if this one passes?
        </P>
        <P>
          That is the idea behind the new Cate Agent. For a code task, a read-only orchestrator sets the goal and
          the verification check. It can spin up one or more attempts in fresh Git worktrees. Per-attempt drivers
          launch coding-agent CLIs in real canvas terminals, and when an attempt finishes a separate verifier
          checks it. From there the orchestrator either takes a passing result or starts another round from the
          failure evidence. The finished work sits in a review state until you merge it, open it as a pull request,
          or throw it away.<Fn n={6} />
        </P>
        <P>
          The reason the loop stays legible is that each part has a place you can point at. Attempts live in
          their own worktrees and terminals. Verification gets its own terminal. The result you kept stays
          visibly separate from the attempts you rejected. The canvas lays the process out as a workspace instead
          of flattening it into a single scrolling chat.
        </P>
        <P>
          So designing a loop turns out to be mostly structural work. You give a repeated process a shape:
          something concrete to satisfy, evidence to check it against, a little state to carry forward, limits
          that genuinely halt it, and an ending a person can still read. Getting the agent to run longer is
          almost beside the point.
        </P>
        <P>
          The model does the individual steps. Whether those steps add up to software you can actually ship is a
          property of the loop you built around it, and that part is still your job.
        </P>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="References">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
